import React, { useCallback, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuthStore, useToastStore, useUIStore } from '@/stores';
import { useTour } from './TourProvider';
import { persistOnboardingSeenVersion } from '../hooks/tour-persistence';
import { CURRENT_TOUR_VERSION } from '../steps/tour-steps';
import { releaseNotesSince } from '../steps/release-notes';

/**
 * Per-version, per-tab dismissal flag for the banner. Using sessionStorage
 * (not localStorage) so a fresh tab re-checks the server state — important
 * when an admin force-resets a user mid-day. Key includes CURRENT_TOUR_VERSION
 * so a deploy that bumps the version automatically re-shows the banner for
 * users who have not caught up.
 */
const sessionDismissKey = (version: number): string =>
  `lr.onboarding.whats_new_dismissed_session.v${version}`;

const isSessionDismissed = (version: number): boolean => {
  try {
    return window.sessionStorage.getItem(sessionDismissKey(version)) === '1';
  } catch {
    return false;
  }
};

const setSessionDismissed = (version: number): void => {
  try {
    window.sessionStorage.setItem(sessionDismissKey(version), '1');
  } catch {
    // Best-effort.
  }
};

/**
 * Banner left-offset = sidebar width + a 16px gutter, so it sits just past the
 * sidebar's "Take a tour" button and tracks the collapse state. Mirrors the
 * sidebar's own width classes (`Sidebar.tsx`: collapsed `w-18` = `spacing.18`
 * = 4.5rem = 72px; expanded `w-64` = 16rem = 256px — see `tailwind.config`).
 * Named here so the magic numbers and the coupling are explicit; keep them in
 * sync if the sidebar width ever changes.
 */
const BANNER_LEFT_COLLAPSED = 'left-[88px]'; // 72px sidebar + 16px gutter
const BANNER_LEFT_EXPANDED = 'left-[272px]'; // 256px sidebar + 16px gutter

/**
 * "What's new" toast. Renders when the authenticated user has seen a
 * previous tour version but not the current one. Two affordances:
 *  - Show me → starts the tour in 'whats-new' mode (filtered to steps
 *    with addedInVersion > seenVersion).
 *  - Dismiss / X → PATCHes seen_version → CURRENT_TOUR_VERSION (treats
 *    as completed; no re-prompting next session).
 *
 * Hidden when the tour is active (so it doesn't compete with TourCard
 * for attention) and on viewports below 1024px (matches the rest of
 * the tour's mobile policy).
 */
export const WhatsNewBanner: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const addToast = useToastStore((s) => s.addToast);
  // Anchor the banner just to the right of the sidebar; its width depends on
  // the collapse state (w-18 = 72px collapsed, w-64 = 256px expanded), so the
  // left offset follows it and the banner sits beside the "Take a tour" button.
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const tour = useTour();

  const [sessionDismissed, setSessionDismissedState] = useState<boolean>(() =>
    isSessionDismissed(CURRENT_TOUR_VERSION),
  );

  const seenVersion = user?.onboardingSeenVersion ?? 0;
  const eligible = !!user && seenVersion > 0 && seenVersion < CURRENT_TOUR_VERSION;

  // Count of new-since-seen release notes. Sourced from the decoupled
  // release-notes registry — the same source TourProvider walks in 'whats-new'
  // mode — so the headline matches what the user will then be shown.
  const newCount = useMemo(
    () => releaseNotesSince(seenVersion).length,
    [seenVersion],
  );

  const handleShowMe = useCallback(() => {
    setSessionDismissed(CURRENT_TOUR_VERSION);
    setSessionDismissedState(true);
    tour.start({ mode: 'whats-new' });
  }, [tour]);

  const handleDismiss = useCallback(() => {
    setSessionDismissed(CURRENT_TOUR_VERSION);
    setSessionDismissedState(true);
    // Reuse the shared helper used by Skip / Done in TourProvider so the
    // dismissed flag, optimistic local update, retry-once policy, and
    // toast-on-second-failure are identical to the in-tour exit paths.
    // Without this, a banner-Dismiss whose PATCH failed would silently
    // leave the user replaying the banner on the next session (server
    // still says old seenVersion + no localStorage flag to suppress).
    if (user && seenVersion < CURRENT_TOUR_VERSION) {
      void persistOnboardingSeenVersion(user, setUser, addToast);
    }
  }, [user, seenVersion, setUser, addToast]);

  if (!eligible) return null;
  if (sessionDismissed) return null;
  if (tour.isActive) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      // Bottom-left, beside the sidebar's "Take a tour" button (the banner's
      // "Show me" launches the same tour, so co-locating them ties the
      // affordances together). Left offset clears the sidebar and tracks its
      // collapse state; bottom-4 lands it level with the lower nav. z-50 sits
      // above page content but below the tour overlay.
      className={clsx(
        'fixed bottom-4 z-50 hidden max-w-sm rounded-md border border-gray-200 bg-white p-4 shadow-lg lg:block',
        'transition-[left] duration-200 ease-in-out',
        sidebarCollapsed ? BANNER_LEFT_COLLAPSED : BANNER_LEFT_EXPANDED,
      )}
    >
      <div className="flex items-start gap-3">
        <SparklesIcon
          className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-600"
          aria-hidden="true"
        />
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-900">
            {newCount > 0
              ? `${newCount} new ${newCount === 1 ? 'feature' : 'features'} since you were here`
              : 'New features available'}
          </p>
          <p className="mt-1 text-xs text-gray-600">
            Take a one-minute tour through what changed.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={handleShowMe}
              className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700"
            >
              Show me
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="rounded-md px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100"
            >
              Dismiss
            </button>
          </div>
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={handleDismiss}
          className="-m-1 flex-shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};
